import classnames from 'classnames';
import { isEqual } from 'lodash';
import Async from 'react-select/async';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import type { IOption } from '../interfaces/option';
import { translateKey } from '../../translate';
import { Feedback } from '../elements/common';
import { Component } from '../elements/Component';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

interface IDropdownAsyncLoadInputProps<T> {
    automation?: string;
    clearable?: boolean;
    disabled?: boolean;
    error?: string;
    loadOptions: (input: string) => Promise<IOption<T>[]>;
    newstyles?: boolean;
    noOptionsMessage?: React.ReactNode;
    onChange: (value: T | null, label?: string) => Promise<void> | void;
    onDoValidate?: (value: T | null) => Promise<void>;
    onInputChange?: (input: string) => string;
    placeholder?: string;
    refreshToken?: string;
    value: T | null;
}

interface IDropdownSearchState<T> {
    options: IOption<T>[];
    selectedOption?: IOption<T>;
}

export class DropdownAsyncLoadInput<T> extends Component<
    FormContextProps<IDropdownAsyncLoadInputProps<T>>,
    IDropdownSearchState<T>
> {
    private _isMounted: boolean;

    public async componentDidMount() {
        this._isMounted = true;
        const options = await this.props.loadOptions('');
        if (this._isMounted && options) {
            await this.setState({ options });
        }
    }

    public async componentDidUpdate() {
        if (this._isMounted && this.props.value === null && this.state.selectedOption) {
            await this.setState({ selectedOption: undefined });
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        return (
            <FormContextConsumer {...this.props}>
                {(properties) => {
                    const noSelection = !properties.value;
                    const selectClassName = classnames(
                        properties.newstyles ? 'select-form-control-ds' : 'select-form-control',
                        properties.error ? 'has-danger' : 'no-danger',
                        {
                            pristine: noSelection,
                            'selected-option': this.state.selectedOption,
                        },
                        this.props.automation ? 'automation-' + this.props.automation : ''
                    );
                    const clearable = properties.clearable === undefined ? true : properties.clearable;

                    return (
                        <>
                            <Async<IOption<T>>
                                backspaceRemovesValue={clearable}
                                className={selectClassName}
                                classNamePrefix="Select"
                                defaultOptions
                                isClearable={clearable}
                                isDisabled={properties.disabled}
                                isMulti={false}
                                key={properties.refreshToken || 'select'}
                                loadOptions={async (input: string) => properties.loadOptions(input)}
                                noOptionsMessage={() =>
                                    this.props.noOptionsMessage || translateKey('common.typeToSearch')
                                }
                                onChange={async (e) => {
                                    if (e && !Array.isArray(e) && e.value) {
                                        const value = e.value;
                                        await properties.onChange(value, e.label);
                                        await this.setState({ selectedOption: { value, label: e.label } });
                                    } else {
                                        await properties.onChange(null);
                                        await this.setState({ selectedOption: undefined });
                                    }

                                    if (properties.onDoValidate) {
                                        await properties.onDoValidate(e?.value ?? null);
                                    }
                                }}
                                onInputChange={properties.onInputChange}
                                placeholder={properties.placeholder || `${translateKey('common.search')}...`}
                                value={this.getOption(properties.value)}
                            />
                            <Feedback
                                error={properties.error}
                                key="feedback"
                            />
                        </>
                    );
                }}
            </FormContextConsumer>
        );
    }

    private getOption(propValue: T | null) {
        if (propValue && this.state.selectedOption && isEqual(this.state.selectedOption.value, propValue)) {
            return this.state.selectedOption;
        }

        if (propValue && this.state.options?.length > 0) {
            return this.state.options.find((option) => isEqual(option.value, propValue));
        }

        return null;
    }
}

export function DropdownAsyncLoadField<T>(props: FormContextProps<IDropdownAsyncLoadInputProps<T>> & IFieldProps) {
    return (
        <Field {...props}>
            <DropdownAsyncLoadInput<T> {...props} />
        </Field>
    );
}
